import {
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  Pill,
  Text,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { formatToReadableTime } from "../../../utils/utils";
import { sortByLastName } from "../../Assessment/Rater/utils";
import { Card } from "../../Card";
import { MULTI, Selector } from "../../Selectors/Selector";
import { TermSelect } from "../../TermManagement/Terms/TermSelect";
import { CourseReviewContext } from "../CourseReview";

const FacultyActions = () => {
  const { t } = useTranslation();
  const { actions, actionTypes, draftData, setDraftData } =
    useContext(CourseReviewContext);
  const location = useLocation();
  const { relevantTerms, stats = {} } = location.state;
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);

  const sortedAuthors = Array.from(new Set(actions.map((r) => r.author)))
    .map((author) => ({ label: author, id: author, value: author }))
    .sort(sortByLastName);

  useEffect(() => {
    let filteredActions = actions.filter((a) =>
      relevantTerms.find((t) => t.id === a.term_id)
    );
    if (selectedTerms.length > 0) {
      filteredActions = filteredActions.filter((action) =>
        selectedTerms.find((term) => term === action.term_id)
      );
    }
    if (selectedAuthors.length > 0) {
      filteredActions = filteredActions.filter((action) =>
        selectedAuthors.find((author) => author === action.author)
      );
    }
    if (selectedActionTypes.length > 0) {
      filteredActions = filteredActions.filter((action) =>
        selectedActionTypes.find((typeId) => typeId === action.action_type.id)
      );
    }
    setFilteredActions(filteredActions);
  }, [
    actions,
    selectedTerms,
    selectedAuthors,
    selectedActionTypes,
    relevantTerms,
  ]);

  function sortedActions(actions) {
    return actions.sort((a, b) => {
      if (a.action_type.name !== b.action_type.name) {
        return a.action_type.name.localeCompare(b.action_type.name);
      }
      return new Date(a.timestamp) - new Date(b.timestamp);
    });
  }

  function actionIsDisabled(action) {
    return draftData.selectedActions.some(
      (a) =>
        a.id.split("=")[0] === action.id.split("=")[0] ||
        a.action_type.id === action.action_type.id
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Heading level="h3" as="h2">
          {t("courseReview.facultyActions")}
        </Heading>
        {stats.section_count > 0 && (
          <Text>
            {t("courseReview.sectionsWithActions", {
              num: stats.actions_count,
              total: stats.section_count,
            })}
          </Text>
        )}
      </div>
      <View
        as="div"
        borderWidth="0 0 small 0"
        width="100%"
        margin="xxx-small 0 small 0"
      />
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <div style={{ width: "33%" }}>
          <TermSelect
            id="faculty-actions-term-select"
            label={t("terms")}
            selectHandler={(terms) => setSelectedTerms(terms)}
            selectedTerms={selectedTerms}
            size="small"
            filteredTermIds={relevantTerms.map((t) => t.id)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <Selector
            id="faculty-actions-author-select"
            type={MULTI}
            label={t("global.authors")}
            selectHandler={(authors) => setSelectedAuthors(authors)}
            selectedOptions={selectedAuthors}
            size="small"
            options={sortedAuthors}
          />
        </div>
        <div style={{ width: "33%" }}>
          <Selector
            id="faculty-actions-action-type-select"
            type={MULTI}
            label={t("actions.actionTypes")}
            selectHandler={(types) => setSelectedActionTypes(types)}
            selectedOptions={selectedActionTypes}
            size="small"
            options={actionTypes.map((type) => ({ ...type, label: type.name }))}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: "0.75rem",
          margin: "-0.75rem",
          overflowY: "auto",
        }}
      >
        {filteredActions.length === 0 ? (
          <Text>{t("actions.noActions")}</Text>
        ) : (
          <Flex wrap="wrap" gap="medium">
            {sortedActions(filteredActions).map((action, key) => (
              <Flex.Item width="100%" key={key}>
                <Card style={{ border: "1px solid #ccc", padding: "10px" }}>
                  <Flex alignItems="start" gap="small" wrap="wrap">
                    <Flex.Item shouldShrink shouldGrow>
                      <Text as="p" fontStyle="italic" size="small">
                        {action.author}
                        {" - "}
                        {formatToReadableTime(new Date(action.timestamp + "Z"))}
                      </Text>
                      <Pill color="info" margin="small 0 0 0">
                        {action.action_type.name}
                      </Pill>
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        screenReaderLabel={t("courseReview.addAction")}
                        disabled={actionIsDisabled(action)}
                        onClick={() => {
                          setDraftData((prev) => ({
                            ...prev,
                            selectedActions: [action, ...prev.selectedActions],
                          }));
                        }}
                        renderIcon={IconAddLine}
                        color="success"
                        size="small"
                      />
                    </Flex.Item>
                    <Flex.Item size="100%">
                      <Text as="p" size="small">
                        {action.text}
                      </Text>
                    </Flex.Item>
                  </Flex>
                </Card>
              </Flex.Item>
            ))}
          </Flex>
        )}
      </div>
    </div>
  );
};

export default FacultyActions;
